/**
 * TaskCard — Draggable task card for Kanban view
 */

import { Calendar, GripVertical, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { AMTask } from "@/hooks/useAMTasks";

const priorityConfig = {
  urgent: { label: "Urgent", className: "bg-red-100 text-red-700 border-red-200" },
  high: { label: "High", className: "bg-orange-100 text-orange-700 border-orange-200" },
  medium: { label: "Medium", className: "bg-blue-100 text-blue-700 border-blue-200" },
  low: { label: "Low", className: "bg-slate-100 text-slate-600 border-slate-200" },
};

const sourceLabels: Record<string, string> = {
  manual: "",
  auto_health: "Health",
  auto_sla: "SLA",
  auto_contract: "Contract",
  auto_engagement: "Engagement",
};

interface TaskCardProps {
  task: AMTask;
  agentName?: string;
  onClick: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
}

export default function TaskCard({ task, agentName, onClick, draggable, onDragStart }: TaskCardProps) {
  const prio = priorityConfig[task.priority];
  const sourceLabel = sourceLabels[task.source];
  const isOverdue = task.dueDate && task.status !== "done" && new Date(task.dueDate) < new Date();

  return (
    <div
      className="bg-card border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow group"
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
    >
      <div className="flex items-start gap-2">
        {draggable && (
          <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground line-clamp-2">{task.title}</p>

          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${prio.className}`}>
              {prio.label}
            </Badge>
            {sourceLabel && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {sourceLabel}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            {agentName && (
              <span className="flex items-center gap-1 truncate">
                <User className="h-3 w-3" />
                {agentName}
              </span>
            )}
            {task.dueDate && (
              <span className={`flex items-center gap-1 ${isOverdue ? "text-red-600 font-medium" : ""}`}>
                <Calendar className="h-3 w-3" />
                {new Date(task.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
